import type { FreightHazmatInfo } from '../types/ltl';

export const RequiredHazmatFields = {
  identificationNumber: 'identificationNumber',
  properShippingName: 'properShippingName',
  hazardClass: 'hazardClass',
  packingGroup: 'packingGroup',
  emergencyContactName: 'emergencyContactName',
  emergencyPhone: 'emergencyPhone',
} as const;

export type RequiredHazmatField = (typeof RequiredHazmatFields)[keyof typeof RequiredHazmatFields];

const isRequiredFieldFilled = (info: FreightHazmatInfo, field: RequiredHazmatField): boolean =>
  field === 'hazardClass' ? !!info.hazardClass?.length : !!info[field];

/**
 * Returns true when any hazmat field (required or not) has a meaningful value.
 * `flashpointTemperatureF` uses != null rather than !! so that 0 °F counts as filled.
 * Used to determine when required-field validation should activate.
 */
export const isAnyHazmatFieldFilled = (info: FreightHazmatInfo | null): boolean =>
  !!info &&
  !!(
    info.identificationNumber ||
    info.properShippingName ||
    info.hazardClass?.length ||
    info.packingGroup ||
    info.emergencyContactName ||
    info.emergencyPhone ||
    info.emergencyResponseReference ||
    info.flashpointTemperatureF != null ||
    info.additionalDetails
  );

export const getMissingRequiredHazmatFields = (info: FreightHazmatInfo | null): Set<RequiredHazmatField> => {
  if (!isAnyHazmatFieldFilled(info)) return new Set();
  return new Set(Object.values(RequiredHazmatFields).filter((f) => !isRequiredFieldFilled(info!, f)));
};

export const isHazmatReadyForQuote = (info: FreightHazmatInfo | null): boolean => getMissingRequiredHazmatFields(info).size === 0;

export const areAllCommoditiesHazmatReady = (handlingUnits: { commodities: { hazmatInfo: FreightHazmatInfo | null }[] }[]): boolean =>
  handlingUnits.every((u) => u.commodities.every((c) => isHazmatReadyForQuote(c.hazmatInfo)));
